// Following a shared chat live (collaboration v1, PR 2 §6; the server half is
// src/chat-follow.mjs).
//
// A reader's chat pane never sends a turn. Instead it attaches to the owner's
// running turn through GET /api/chat/follow/:threadId, which replays that
// turn's byte log from its first byte, and it polls GET /api/v1/threads/:id/
// state — every 2 s while the tab is focused, every 10 s otherwise — to learn
// when a new turn starts (the pane remounts to attach), when the saved copy
// of an answer lands (it is merged into place by message id, never remounted),
// whether the owner stopped the answer, and when the chat stops being
// readable (the calm "made private" page).
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResumableClientStorage } from '@assistant-ui/react-ai-sdk';
import { apiUrl, authHeaders } from './shell';
import { createResilientFetch } from './resilient-fetch';
import { answerResumeApi } from './answer-resume';
import { toolSourcesFrom, type ServerThreadMeta, type ToolSources } from '@/server-threads';

/** What a follower's browser polls (GET /api/v1/threads/:id/state). */
export interface ThreadLiveState {
  updatedAt: number;
  /** Owner only: the exact running stream that Stop may cancel. */
  streamId?: string | null;
  /** A turn is being answered right now. */
  active: boolean;
  /** People following the running turn. */
  followers: number;
  /** The owner stopped the chat's last answer. */
  stopped: boolean;
  /** Recorded tool calls whose result came from someone else's account. */
  toolSources: ToolSources;
  /** Owner only: forks of this chat, and how many people made them. */
  forks?: number;
  forkedBy?: number;
}

export type ThreadStateRead =
  | { status: 'ok'; state: ThreadLiveState }
  | { status: 'gone' }
  | { status: 'error' };

const count = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;

/** A well-formed state answer, or null. */
export function threadLiveStateFrom(value: unknown): ThreadLiveState | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.updatedAt !== 'number' || typeof raw.active !== 'boolean') return null;
  return {
    updatedAt: raw.updatedAt,
    ...(typeof raw.streamId === 'string' || raw.streamId === null
      ? { streamId: raw.streamId as string | null }
      : {}),
    active: raw.active,
    followers: count(raw.followers),
    stopped: raw.stopped === true,
    toolSources: toolSourcesFrom(raw.toolSources),
    ...(typeof raw.forks === 'number' ? { forks: count(raw.forks) } : {}),
    ...(typeof raw.forkedBy === 'number' ? { forkedBy: count(raw.forkedBy) } : {}),
  };
}

/** Read a chat's live state. 401/403/404 mean the person can no longer read it. */
export async function readThreadState(
  threadId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<ThreadStateRead> {
  try {
    const response = await fetch(apiUrl(`/api/v1/threads/${encodeURIComponent(threadId)}/state`), {
      credentials: 'same-origin',
      headers: { ...authHeaders() },
      signal,
    });
    if ([401, 403, 404].includes(response.status)) return { status: 'gone' };
    if (!response.ok) return { status: 'error' };
    const state = threadLiveStateFrom(await response.json());
    return state ? { status: 'ok', state } : { status: 'error' };
  } catch {
    return { status: 'error' };
  }
}

// ── The follower's transport ────────────────────────────────────────────────

/** How a follow attempt ended, as the pane reports it to the controller. */
export type FollowOutcome =
  | 'attached' // streaming the owner's turn
  | 'idle' // nothing was running
  | 'ended' // the followed turn's stream finished (or stopped)
  | 'unavailable' // 409: not the running turn, or replay no longer complete
  | 'busy' // 429: the follower slots are taken
  | 'gone' // 401/403/404: no longer readable
  | 'failed'; // anything else

export const followStreamId = (threadId: string) => `follow:${threadId}`;

export const followUrl = (threadId: string, stream?: string) =>
  apiUrl(
    `/api/chat/follow/${encodeURIComponent(threadId)}${stream ? `?stream=${encodeURIComponent(stream)}` : ''}`,
  );

/** In-memory stream storage for one follow mount: the runtime's mount-time
 * resume attaches to `follow:<threadId>` once; nothing is persisted, so a
 * reload follows again instead of resuming a stream it never owned. */
export function createFollowStorage(threadId: string): ResumableClientStorage {
  let pending: string | null = followStreamId(threadId);
  return {
    getStreamId: () => pending,
    setStreamId: () => {},
    clear: () => {
      pending = null;
    },
  };
}

const OUTCOMES: Record<number, FollowOutcome> = {
  204: 'idle',
  401: 'gone',
  403: 'gone',
  404: 'gone',
  409: 'unavailable',
  429: 'busy',
};

// A followed turn that breaks off ends quietly: the saved answer arrives by
// polling, so a reader never sees an error for the owner's network or stop.
function quietEnd(body: ReadableStream<Uint8Array>, onEnd: () => void) {
  const reader = body.getReader();
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    onEnd();
  };
  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          end();
          controller.close();
        } else controller.enqueue(value);
      } catch {
        end();
        controller.close();
      }
    },
    cancel(reason) {
      end();
      void reader.cancel(reason).catch(() => {});
    },
  });
}

/**
 * The follower's fetch: the resilient re-attach (same byte skipping as the
 * owner's resume, against the follow route with `?stream=`), which never
 * sends a stop for someone else's turn. Anything but a stream reads as 204
 * ("nothing to resume") to the chat runtime, and `onOutcome` hears why.
 */
export function createFollowFetch({
  threadId,
  onOutcome,
  onRetryAfter,
  baseFetch,
  answerReaderAdmitted = false,
}: {
  threadId: string;
  onOutcome: (outcome: FollowOutcome) => void;
  onRetryAfter?: (seconds: number) => void;
  baseFetch?: typeof fetch;
  answerReaderAdmitted?: boolean;
}): typeof fetch {
  const resilient = createResilientFetch({
    resumeUrl: answerResumeApi(
      threadId,
      apiUrl,
      (stream) => followUrl(threadId, stream),
      answerReaderAdmitted,
    ),
    headers: () => authHeaders(),
    cancelOnAbort: false,
    ...(baseFetch ? { baseFetch } : {}),
  });
  return async (input, init) => {
    let response: Response;
    try {
      response = await resilient(input, init);
    } catch (error) {
      if (init?.signal?.aborted) throw error;
      onOutcome('failed');
      return new Response(null, { status: 204 });
    }
    if (response.status === 200 && response.body) {
      onOutcome('attached');
      return new Response(
        quietEnd(response.body, () => onOutcome('ended')),
        { status: 200, headers: response.headers },
      );
    }
    if (response.status === 429)
      onRetryAfter?.(Number(response.headers.get('retry-after')) || FOLLOW_BUSY_SECONDS);
    onOutcome(OUTCOMES[response.status] ?? 'failed');
    await response.body?.cancel().catch(() => {});
    return new Response(null, { status: 204 });
  };
}

// ── The controller the App uses ─────────────────────────────────────────────

export const FOLLOW_POLL_FOCUSED_MS = 2_000;
export const FOLLOW_POLL_BACKGROUND_MS = 10_000;
export const FOLLOW_BUSY_SECONDS = 5;

const focused = () =>
  typeof document !== 'undefined' &&
  document.visibilityState === 'visible' &&
  (typeof document.hasFocus !== 'function' || document.hasFocus());

/** Whether the open chat is worth watching: someone else's (a reader), a
 * shared chat of the owner's ("N following"), or a run chat (PR 4). */
export const watchesChat = (meta: ServerThreadMeta | null) =>
  Boolean(meta && (meta.access === 'viewer' || meta.visibility !== 'private' || meta.agentRunId));

type Controller = {
  /** A turn may be followed again: nothing was running, or the last one ended
   * and the chat has since been idle. */
  armed: boolean;
  busyUntil: number;
  updatedAt: number | null;
};

/**
 * Polls a watched chat's state and decides when its pane re-attaches.
 * `refresh()` re-reads the chat (messages and meta) and must resolve before
 * a remount, so the new mount opens at the saved messages. The caller sets
 * `followingRef.current` on every render: whether the open pane follows (a
 * reader, or the owner watching their own run).
 */
export function useChatFollow({
  threadId,
  meta,
  enabled,
  refresh,
}: {
  threadId: string;
  meta: ServerThreadMeta | null;
  enabled: boolean;
  refresh: () => Promise<void>;
}) {
  const [live, setLive] = useState<ThreadLiveState | null>(null);
  const [gone, setGone] = useState(false);
  const [attach, setAttach] = useState(0);
  const control = useRef<Controller>({ armed: false, busyUntil: 0, updatedAt: null });
  const followingRef = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const watched = enabled && watchesChat(meta) && meta?.id === threadId;

  useEffect(() => {
    setLive(null);
    setGone(false);
    control.current = { armed: false, busyUntil: 0, updatedAt: null };
  }, [threadId]);

  useEffect(() => {
    if (!watched) return;
    const run = { stopped: false, timer: undefined as ReturnType<typeof setTimeout> | undefined };
    const decide = (state: ThreadLiveState) =>
      shouldAttach(control.current, state, followingRef.current);
    const adopt = async (state: ThreadLiveState) => {
      const changed = observe(control.current, state);
      if (changed) await refreshRef.current().catch(() => {});
      if (run.stopped) return;
      setLive(state);
      if (!decide(state)) return;
      control.current.armed = false;
      if (!changed) await refreshRef.current().catch(() => {});
      if (run.stopped) return;
      rememberFollowScroll(threadId);
      setAttach((value) => value + 1);
    };
    const poll = async () => {
      const read = await readThreadState(threadId);
      if (run.stopped) return;
      if (read.status === 'gone') return setGone(true);
      if (read.status === 'ok') await adopt(read.state);
      if (run.stopped) return;
      run.timer = setTimeout(
        () => void poll(),
        focused() ? FOLLOW_POLL_FOCUSED_MS : FOLLOW_POLL_BACKGROUND_MS,
      );
    };
    const wake = () => {
      if (!focused()) return;
      clearTimeout(run.timer);
      void poll();
    };
    void poll();
    window.addEventListener('focus', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      run.stopped = true;
      clearTimeout(run.timer);
      window.removeEventListener('focus', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [watched, threadId]);

  /** The pane reports how its follow attempt went. */
  const report = useCallback((outcome: FollowOutcome) => {
    if (outcome === 'gone') setGone(true);
    control.current.armed = ARMS[outcome] ?? control.current.armed;
  }, []);
  const retryAfter = useCallback((seconds: number) => {
    control.current.busyUntil = Date.now() + seconds * 1000;
  }, []);
  /** A new follow mount attaches by itself: do not re-attach it. */
  const disarm = useCallback(() => {
    control.current.armed = false;
  }, []);

  return { live, gone, attach, report, retryAfter, disarm, followingRef };
}

// Whether an outcome leaves the next running turn to be attached: nothing
// was running (idle), or the slots were full (busy, after Retry-After).
// A followed turn that ended waits until the chat was seen idle.
const ARMS: Partial<Record<FollowOutcome, boolean>> = {
  idle: true,
  busy: true,
  attached: false,
  ended: false,
  unavailable: false,
};

// Reconcile the initial snapshot too: a short run may finish after the
// thread was loaded but before this browser sees its first live state.
function observe(control: Controller, state: ThreadLiveState) {
  const changed = control.updatedAt === null || state.updatedAt > control.updatedAt;
  control.updatedAt = state.updatedAt;
  if (!state.active) control.armed = true;
  return changed;
}

function shouldAttach(control: Controller, state: ThreadLiveState, following: boolean) {
  return following && state.active && control.armed && Date.now() >= control.busyUntil;
}

// ── Keeping the saved copy and the reader's place ──────────────────────────

type IdMessage = { id: string; role: string; parts?: unknown };

/**
 * The saved messages with anything this pane has that is not saved (an
 * answer its owner stopped) kept after the message it followed. Returns null
 * when nothing would change, so the runtime is only touched on news. A saved
 * copy replaces the streamed message with the same id in place: the server
 * minted that id, so the answer never jumps or remounts.
 */
export function mergeSavedMessages<T extends IdMessage>(saved: T[], current: T[]): T[] | null {
  const savedIds = new Set(saved.map((message) => message.id));
  const extras = new Map<string | null, T[]>();
  let anchor: string | null = null;
  for (const message of current) {
    if (savedIds.has(message.id)) anchor = message.id;
    else extras.set(anchor, [...(extras.get(anchor) ?? []), message]);
  }
  const merged = [...(extras.get(null) ?? [])];
  for (const message of saved) merged.push(message, ...(extras.get(message.id) ?? []));
  const same =
    merged.length === current.length &&
    merged.every(
      (message, index) =>
        message.id === current[index].id &&
        message.role === current[index].role &&
        JSON.stringify(message.parts) === JSON.stringify(current[index].parts),
    );
  return same ? null : merged;
}

// The follower's scroll position across a re-attach (the pane remounts when
// a new turn starts): kept only when they had scrolled up to read.
let keptScroll: { threadId: string; top: number } | null = null;
const AT_BOTTOM_PX = 32;

export function rememberFollowScroll(threadId: string) {
  keptScroll = null;
  if (typeof document === 'undefined') return;
  const viewport = document.querySelector<HTMLElement>('[data-slot="aui_thread-viewport"]');
  if (!viewport) return;
  const fromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
  if (fromBottom > AT_BOTTOM_PX) keptScroll = { threadId, top: viewport.scrollTop };
}

/** The kept position for this chat's new mount, once. */
export function takeFollowScroll(threadId: string): number | null {
  const kept = keptScroll?.threadId === threadId ? keptScroll.top : null;
  keptScroll = null;
  return kept;
}
